import styled from "@emotion/styled";
import { Box, type Theme, Typography, useTheme } from "@mui/material";
import { colors } from "@zfp-akademie-a-s/zfp-design";
import { type PensionChartData, getPensionChartAnnotations, mapPensionPreviewToChartData } from "Charts/Utils/PensionChartUtils";
import type { PensionGoalModelingPreview } from "Infrastructure/ApiZfp/Schema";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getYearsInflected } from "Infrastructure/Utils/LanguageUtils";
import { Chart } from "chart.js";
import AnnotationPlugin from "chartjs-plugin-annotation";
import { addMonths, format } from "date-fns";
import type React from "react";
import { Line } from "react-chartjs-2";

Chart.register(AnnotationPlugin);

const AGE_TICKS_PLUGIN_ID = "pensionAgeTicks";

type Props = {
  readonly preview: PensionGoalModelingPreview;
  readonly referenceStartDate: Date;
};

type AgeTick = {
  readonly month: number;
  readonly label: string;
};

const Dot = styled.div<{ colorLeft: string; colorRight: string }>`
  background: linear-gradient(to right, ${(props) => props.colorLeft} 50%, ${(props) => props.colorRight} 50%);
  width: 12px;
  height: 12px;
  margin-right: 4px;
  border-radius: ${(props) => props.theme.spacing(Radius.MAX)};
`;

const getLabels = (lengthInMonths: number, referenceStartDate: Date) => {
  return new Array(lengthInMonths).fill("").map((_, index) => {
    return format(addMonths(referenceStartDate, index), "yyyy-MM-dd");
  });
};

// react-chartjs-2 matches datasets across renders by this key and recreates the ones it can't match, which makes
// Chart.js animate them again from zero on every re-render. `label` can't serve as the key: it would leak into the
// tooltip body lines, which are parsed as plain numbers.
const DATASET_ID_KEY = "id";

const getDatasets = (data: PensionChartData) => {
  const datasets = [
    {
      id: "accumulationPesimistic",
      data: data.accumulationPesimistic,
      fill: true,
      borderColor: colors.chartNeutralLight,
      backgroundColor: colors.white,
      pointStyle: false as const,
      order: 1,
    },
    {
      id: "accumulationRealistic",
      data: data.accumulationRealistic,
      fill: true,
      borderColor: colors.chartNeutral,
      backgroundColor: colors.chartNeutralLight,
      pointStyle: false as const,
      borderWidth: 6,
      order: 2,
    },
    {
      id: "accumulationOptimistic",
      data: data.accumulationOptimistic,
      fill: true,
      borderColor: "transparent",
      backgroundColor: colors.chartNeutralLight,
      pointStyle: false as const,
      order: 3,
    },
    {
      id: "pensionWithdrawOptimistic",
      data: data.pensionWithdrawOptimistic,
      fill: true,
      borderColor: "transparent",
      backgroundColor: colors.chartPositiveLight,
      pointStyle: false as const,
      order: 4,
    },
    {
      id: "pensionWithdrawRealistic",
      data: data.pensionWithdrawRealistic,
      fill: true,
      borderColor: colors.chartPositive,
      backgroundColor: colors.chartPositiveLight,
      pointStyle: false as const,
      order: 3,
      borderWidth: 6,
    },
    {
      id: "pensionWithdrawPesimistic",
      data: data.pensionWithdrawPesimistic,
      fill: true,
      borderColor: colors.chartPositiveLight,
      backgroundColor: colors.white,
      pointStyle: false as const,
      order: 0,
    },
  ];

  return datasets;
};

// The x axis holds one point per month, so Chart.js' own tick placement would drop the three ages that actually
// matter (start, retirement, depletion). Draw them directly at their month instead.
const ageTicksPlugin = {
  id: AGE_TICKS_PLUGIN_ID,
  afterDraw: (chart: any, _args: unknown, options: { ticks?: AgeTick[]; color?: string }) => {
    const { ticks, color } = options ?? {};
    if (!ticks?.length) return;

    const xAxis = chart.scales.x;
    const ctx = chart.ctx;

    ctx.save();
    ctx.font = "600 10px Roboto";
    ctx.fillStyle = color ?? "#000";
    ctx.textAlign = "center";

    for (const tick of ticks) {
      ctx.fillText(tick.label, xAxis.getPixelForValue(tick.month), xAxis.bottom + 12);
    }

    ctx.restore();
  },
};

export const PensionChart: React.FC<Props> = ({ preview, referenceStartDate }) => {
  const theme = useTheme();
  const { t } = useResource();

  const data = mapPensionPreviewToChartData(preview);

  if (!data) return null;

  const clientAge = preview.derived.clientAge;
  const ageAtMonth = (month: number) => `${clientAge + Math.round(month / 12)} ${t(Resources.Common.Years)}`;

  const ageTicks: AgeTick[] = [
    { month: 0, label: ageAtMonth(0) },
    { month: data.retirementMonth, label: ageAtMonth(data.retirementMonth) },
    {
      month: data.depletionMonth ?? data.totalMonths - 1,
      label: ageAtMonth(data.depletionMonth ?? data.totalMonths - 1),
    },
  ];

  const options = {
    locale: "en-US",
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      [AGE_TICKS_PLUGIN_ID]: {
        ticks: ageTicks,
        color: colors.textPrimary,
      },
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      annotation: {
        annotations: {
          ...getPensionChartAnnotations({ data }),
        },
      },
      tooltip: {
        enabled: false,
        external: (context: any) => {
          let tooltipEl = document.getElementById("chartjs-tooltip");

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.innerHTML = "<table></table>";
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
          }

          tooltipEl.classList.remove("above", "below", "no-transform");
          if (tooltipModel.yAlign) {
            tooltipEl.classList.add(tooltipModel.yAlign);
          } else {
            tooltipEl.classList.add("no-transform");
          }

          function getBody(bodyItem: any) {
            return bodyItem.lines;
          }

          if (tooltipModel.body) {
            const bodyLines = tooltipModel.body.map(getBody);
            let innerHtml = "";

            const dataIndex = tooltipModel.dataPoints[0].dataIndex;
            const date = format(new Date(tooltipModel.dataPoints[0].label), "M/yyyy");

            if (dataIndex <= data.retirementMonth) {
              const years = Math.floor(dataIndex / 12);
              innerHtml = `
                <tbody>
                  <tr>
                    <td>
                      <div style="display: block;">
                        ${t(Resources.AnalysisDetail.Goals.InvestmentDuration)}: ${years} ${t(getYearsInflected(years))}
                      </div>
                    </td>
                  </tr>
                   <tr>
                    <td>
                      <span style="display: block; margin-bottom: 4px;">${date}</span>
                    </td>
                  </tr>`;
            } else {
              const years = Math.floor((dataIndex - data.retirementMonth) / 12);
              innerHtml = `
                <tbody>
                  <tr>
                    <td>
                      <div style="display: block">
                        ${t(Resources.AnalysisDetail.Goals.DrawProcess)}: ${years} ${t(getYearsInflected(years))}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span style="display: block; margin-bottom: 4px;">${date}</span>
                    </td>
                  </tr>`;
            }

            const pesimistic = bodyLines.length === 6 ? bodyLines[0][0] : bodyLines[0][0];
            const realistic = bodyLines.length === 6 ? bodyLines[2][0] : bodyLines[1][0];
            const optimistic = bodyLines.length === 6 ? bodyLines[3][0] : bodyLines[2][0];

            innerHtml += `
                <tr>
                  <td>
                    <span style="font-weight: 500;">${t(Resources.AnalysisDetail.Goals.ScenarioOptimistic)}</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span>• ${formatCurrency(Number(optimistic.toString().replaceAll(",", "")), CurrencyCode.CZK, 0)}</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span style="font-weight: 500;">${t(Resources.AnalysisDetail.Goals.ScenarioRealistic)}</span>
                  </td>
                </tr>
                <tr>
                  <td>
                     ${Number(realistic.toString().replaceAll(",", "")) < 0 ? `<span>• ${formatCurrency(0, CurrencyCode.CZK, 0)}</span>` : `<span>• ${formatCurrency(Number(realistic.toString().replaceAll(",", "")), CurrencyCode.CZK, 0)}</span>`}
                  </td>
                </tr>

                <tr>
                  <td>
                    <span style="font-weight: 500;">${t(Resources.AnalysisDetail.Goals.ScenarioPesimistic)}</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    ${Number(pesimistic.toString().replaceAll(",", "")) < 0 ? `<span>• ${formatCurrency(0, CurrencyCode.CZK, 0)}</span>` : `<span>• ${formatCurrency(Number(pesimistic.toString().replaceAll(",", "")), CurrencyCode.CZK, 0)}</span>`}
                  </td>
                </tr>
                </tbody>
              `;

            const tableRoot = tooltipEl.querySelector("table");

            if (tableRoot) {
              tableRoot.innerHTML = innerHtml;
            }
          }

          const chartPosition = context.chart.canvas.getBoundingClientRect();
          const tooltipPosition = tooltipEl.getBoundingClientRect();

          const width = tooltipPosition.right - tooltipPosition.left;
          const height = tooltipPosition.bottom - tooltipPosition.top;

          tooltipEl.style.opacity = "1";
          tooltipEl.style.position = "absolute";
          tooltipEl.style.left = `${chartPosition.left + window.pageXOffset + tooltipModel.caretX - width}px`;
          tooltipEl.style.top = `${chartPosition.top + window.pageYOffset + tooltipModel.caretY - height}px`;
          tooltipEl.style.padding = `${tooltipModel.padding}px`;
          tooltipEl.style.pointerEvents = "none";
          tooltipEl.style.zIndex = "10000";

          tooltipEl.style.fontFamily = "Roboto";
          tooltipEl.style.fontSize = "10px";
          tooltipEl.style.lineHeight = "1.1";
          tooltipEl.style.borderWidth = "1px";
          tooltipEl.style.borderStyle = "solid";
          tooltipEl.style.borderColor = colors.borderLight;
          tooltipEl.style.backgroundColor = colors.white;
          tooltipEl.style.borderRadius = theme.spacing(Radius.M);
          tooltipEl.style.padding = theme.spacing(Spacing.M);
        },
      },
    },
    scales: {
      x: {
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
      y: {
        min: 0,
        beginAtZero: true,
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
    },
    layout: {
      padding: { right: 32, bottom: 16 },
    },
  };

  return (
    <Box>
      <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary">
        {t(Resources.Common.Value)}
      </Typography>
      <Box display="flex" alignItems="end">
        <Box position="relative" width={"100%"}>
          <Line
            datasetIdKey={DATASET_ID_KEY}
            options={options}
            plugins={[ageTicksPlugin]}
            data={{
              labels: getLabels(data.totalMonths, referenceStartDate),
              datasets: getDatasets(data),
            }}
          />
        </Box>
        <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary" mb={5.2} ml={-5}>
          {t(Resources.Common.Age)}
        </Typography>
      </Box>

      <Box display="flex" justifyContent="flex-end" columnGap={Spacing.XL}>
        <Box display="flex">
          <Dot colorLeft={colors.chartNeutralLight} colorRight={colors.chartPositiveLight} />
          <Typography fontWeight={500} variant="caption">
            {t(Resources.AnalysisDetail.Goals.ScenarioPesimistic)}
          </Typography>
        </Box>
        <Box display="flex">
          <Dot colorLeft={colors.chartNeutral} colorRight={colors.chartPositive} />
          <Typography fontWeight={500} variant="caption">
            {t(Resources.AnalysisDetail.Goals.ScenarioRealistic)}
          </Typography>
        </Box>
        <Box display="flex">
          <Dot colorLeft={colors.chartNeutralLight} colorRight={colors.chartPositiveLight} />
          <Typography fontWeight={500} variant="caption">
            {t(Resources.AnalysisDetail.Goals.ScenarioOptimistic)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
